import { Link } from "react-router-dom";
import { MapPin, Briefcase, Eye, Star } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { fmtFJD } from "@/lib/api";
import { INDUSTRY_BY_ID } from "@/lib/businesses";

export default function BusinessCard({ business: b }) {
    const industry = INDUSTRY_BY_ID[b.industry];
    const Icon = industry?.icon || Briefcase;
    const photo = b.external_photos?.[0] || industry?.fallback_photo || "https://images.unsplash.com/photo-1483683804023-6ccdb62f86ef?w=1200&q=80";
    return (
        <Link to={`/businesses/${b.id}`} data-testid={`business-card-${b.id}`} className="group">
            <Card className="overflow-hidden border border-border hover:shadow-[0_8px_30px_rgba(10,61,98,0.18)] transition-all duration-300 hover:-translate-y-1 bg-card h-full flex flex-col">
                <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={photo} alt={b.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
                        <Badge className="bg-card/90 backdrop-blur text-foreground border-0 font-app text-[10px] uppercase tracking-wider flex items-center gap-1">
                            <Icon className="h-3 w-3" /> {industry?.label || b.industry}
                        </Badge>
                        {b.real_estate_included && (
                            <Badge className="bg-fiji-green text-white border-0 font-app text-[10px] uppercase tracking-wider">+Real Estate</Badge>
                        )}
                    </div>
                    <div className="absolute bottom-3 right-3 bg-white/95 dark:bg-card/95 backdrop-blur px-3 py-1 rounded-md font-display font-bold text-ocean dark:text-turquoise">
                        {fmtFJD(b.asking_price_fjd)}
                    </div>
                </div>
                <div className="p-5 flex-1 flex flex-col">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground font-app mb-1">
                        <MapPin className="h-3 w-3" /> {b.location_region}, {b.location_island}
                    </div>
                    <h3 className="font-display text-lg font-semibold leading-tight line-clamp-2 mb-2 group-hover:text-ocean dark:group-hover:text-turquoise transition-colors">
                        {b.title}
                    </h3>
                    <p className="text-xs font-app text-muted-foreground line-clamp-2 mb-3 flex-1">{b.public_description}</p>
                    <div className="flex items-center gap-3 text-[11px] font-app text-muted-foreground tabular-nums">
                        {b.revenue_range && <span>Rev {b.revenue_range}</span>}
                        {b.employees && <span>· {b.employees} staff</span>}
                        {b.year_established && <span>· est {b.year_established}</span>}
                        <span className="ml-auto flex items-center gap-2">
                            <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{b.views || 0}</span>
                            {b.watchlist_count > 0 && <span className="flex items-center gap-1 text-coral"><Star className="h-3 w-3" />{b.watchlist_count}</span>}
                        </span>
                    </div>
                </div>
            </Card>
        </Link>
    );
}
